import { createLogger } from '@/lib/logger';
import { withCartSource } from '@/lib/cart-source';

type GiftModalStatus = 'not-eligible' | 'not-needed' | 'user-closed' | 'add-multiple' | 'user-rejected';

type SelectedVariant = {
  id: string;
  price: number;
  available: boolean;
  inventoryQuantity: number;
  inventoryPolicy: 'CONTINUE' | 'DENY';
};

export class SelectOptionsModal extends HTMLElement {
  private logger = createLogger('select-options-modal');
  private productId: string | null = null;
  private productUrl: URL | null = null;
  private selectedVariant: SelectedVariant | null = null;
  private selectedVariantId = '';
  private quantity = 1;
  private quantityInputElem: HTMLInputElement | null = null;
  private requestController: AbortController | null = null;
  private trigger: HTMLElement | null = null;

  connectedCallback() {
    this.addEventListener('click', this.onClick);
    this.addEventListener('change', this.onChange);
    this.addEventListener('submit', this.onSubmit);
    this.addEventListener('keydown', this.onKeydown);
  }

  disconnectedCallback() {
    this.requestController?.abort();
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('submit', this.onSubmit);
    this.removeEventListener('keydown', this.onKeydown);
  }

  private showModal() {
    this.classList.add('select-options-modal--visible');
    this.setAttribute('aria-hidden', 'false');
    this.querySelector<HTMLButtonElement>('[data-modal-close]')?.focus();
  }

  private hideModal() {
    this.requestController?.abort();
    this.classList.remove('select-options-modal--visible');
    this.setAttribute('aria-hidden', 'true');
    this.setAttribute('aria-busy', 'false');
    this.trigger?.focus();
  }

  private onClick = (event: Event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('[data-modal-close], .select-options-modal--backdrop')) {
      this.hideModal();
    } else if (event.target.closest('[data-decrease-quantity]')) {
      this.setQuantity(this.quantity - 1);
    } else if (event.target.closest('[data-increase-quantity]')) {
      this.setQuantity(this.quantity + 1);
    }
  };

  private onChange = (event: Event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (event.target.matches('[data-quantity-input]')) {
      this.setQuantity(Number(event.target.value));
    } else if (event.target.matches('[data-option-input]') && this.productUrl) {
      const url = new URL(this.productUrl);
      const values = Array.from(
        this.querySelectorAll<HTMLInputElement>('[data-option-input]:checked'),
        (input) => input.dataset.optionValueId ?? '',
      );
      url.searchParams.delete('variant');
      url.searchParams.set('option_values', values.join(','));
      void this.loadSection(url, this.productId!, false, event.target.id);
    }
  };

  private onSubmit = (event: Event) => {
    if (!(event.target instanceof HTMLFormElement) || !event.target.matches('[data-product-form]')) return;
    event.preventDefault();
    if (this.getAttribute('aria-busy') === 'true' || !this.selectedVariant?.available) return;
    this.setQuantity(Number(this.quantityInputElem?.value ?? 1));
    const button = this.querySelector<HTMLButtonElement>('[data-add-to-cart]');
    if (button) button.disabled = true;
    void this.onAddToCart().finally(() => {
      if (button) button.disabled = !this.selectedVariant?.available;
    });
  };

  private onKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') this.hideModal();
  };

  private setQuantity(value: number) {
    const variant = this.selectedVariant;
    const max = variant?.inventoryPolicy === 'DENY' ? Math.max(1, variant.inventoryQuantity) : Infinity;
    this.quantity = Math.min(max, Math.max(1, Math.floor(value) || 1));
    if (this.quantityInputElem) this.quantityInputElem.value = String(this.quantity);
  }

  private async loadSection(url: URL, productId: string, autoOpen: boolean, focusId?: string) {
    this.requestController?.abort();
    const controller = new AbortController();
    this.requestController = controller;
    this.setAttribute('aria-busy', 'true');
    const button = this.querySelector<HTMLButtonElement>('[data-add-to-cart]');
    if (button) button.disabled = true;
    url.searchParams.set('section_id', this.dataset.sectionId ?? 'select-options-modal');

    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error(`Could not render product options: ${response.status}`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const content = html.querySelector<HTMLElement>('select-options');
      const data = content?.querySelector('[data-selected-variant]')?.textContent;
      if (!content || content.dataset.productId !== productId || !data) {
        throw new Error('Product options section is missing or belongs to another product');
      }
      const variant = JSON.parse(data) as SelectedVariant | null;
      if (controller.signal.aborted) return;
      const scrollSelector = '[data-options-form], [data-option-list]';
      const scrollPositions = Array.from(this.querySelectorAll<HTMLElement>(scrollSelector), (elem) => [
        elem.scrollTop,
        elem.scrollLeft,
      ]);
      this.innerHTML = content.innerHTML;
      // Re-rendering resets scroll; restore it so the selected swatch stays in view.
      if (!autoOpen) {
        this.querySelectorAll<HTMLElement>(scrollSelector).forEach((elem, index) => {
          const [top, left] = scrollPositions[index] ?? [0, 0];
          elem.scrollTo(left, top);
        });
      }
      this.productId = productId;
      this.dataset.productId = productId;
      this.productUrl = url;
      this.selectedVariant = variant;
      this.selectedVariantId = variant?.id ?? '';
      this.quantityInputElem = this.querySelector('[data-quantity-input]');
      this.setQuantity(1);
      if (autoOpen) this.showModal();
      else if (focusId) this.querySelector<HTMLInputElement>(`#${CSS.escape(focusId)}`)?.focus({ preventScroll: true });
    } catch (error) {
      if (controller.signal.aborted) return;
      this.logger.error('Could not load product options', error);
      if (autoOpen) {
        url.searchParams.delete('section_id');
        window.location.assign(url.href);
      } else {
        this.querySelectorAll<HTMLInputElement>('[data-option-input]').forEach((input) => {
          input.checked = input.defaultChecked;
        });
      }
    } finally {
      if (this.requestController === controller) {
        this.setAttribute('aria-busy', 'false');
        const currentButton = this.querySelector<HTMLButtonElement>('[data-add-to-cart]');
        if (currentButton) currentButton.disabled = !this.selectedVariant?.available;
      }
    }
  }

  async handleRequest(productId: string, productUrl?: string) {
    const trigger = document.activeElement;
    this.trigger = trigger instanceof HTMLElement ? trigger : null;
    const card = document.querySelector<HTMLElement>(`[data-product-id="${CSS.escape(productId)}"]`);
    const href = productUrl ?? card?.querySelector<HTMLAnchorElement>('a[href*="/products/"]')?.href;
    if (!href) {
      this.logger.error(`Missing product URL for ${productId}`);
      return;
    }
    const handle = href.split('/products/').pop()?.split(/[?#/]/)[0];
    if (!handle) return;
    const url = new URL(`${window.Shopify.routes.root}products/${handle}`, window.location.origin);
    await this.loadSection(url, productId, true);
  }

  private async runPurchaseGiftModalFlow(): Promise<{
    status: GiftModalStatus;
  } | null> {
    const manager = window.PurchaseGiftManager;
    if (!manager || typeof manager.showModal !== 'function') return null;

    const selectedVariant = this.selectedVariant;
    const currentVariantId = this.selectedVariantId;

    if (!selectedVariant || !currentVariantId) {
      console.error(
        '[select-options-modal] Missing selected variant data; cannot run PurchaseGiftManager.showModal overrides',
      );
      return manager.showModal();
    }

    const modalViewport = document.querySelector<HTMLElement>('.product-gift-modal__viewport') ?? undefined;
    const packMultiple =
      document.querySelector('[data-bundle-option].active')?.getAttribute('data-pack-multiple') || undefined;

    const skipLabel =
      window.theme?.labels?.product_gift_skip_cta_label?.replace('{qty}', this.quantity.toString()) ||
      window.theme?.labels?.addToCart ||
      '';

    const activeCurrency = window.Shopify.currency.active;
    const baseCurrency = window.theme.shopCurrency;
    const baseCurrencyPriceCents =
      activeCurrency === baseCurrency
        ? selectedVariant.price
        : (window.Currency?.convert(selectedVariant.price, activeCurrency, baseCurrency) ?? selectedVariant.price);

    return manager.showModal({
      currentVariantId,
      variant: {
        price: baseCurrencyPriceCents,
        inventory_quantity: selectedVariant.inventoryQuantity,
      },
      qty: this.quantity,
      ...(packMultiple ? { packMultiple } : {}),
      ...(modalViewport ? { modalViewport } : {}),
      ...(skipLabel ? { skipLabel } : {}),
    });
  }

  private async onAddToCart() {
    this.logger.log(`Adding ${this.quantity} of variant ${this.selectedVariantId} to cart`);

    let shouldHidePopover = true;

    try {
      const giftModalResult = await this.runPurchaseGiftModalFlow();

      if (giftModalResult?.status === 'user-closed') {
        shouldHidePopover = false;
        return;
      }

      if (giftModalResult?.status === 'add-multiple') {
        const requiredAmount = Number(window.theme?.gift_product?.required_amount ?? 0);
        if (requiredAmount > 0) {
          this.quantity = requiredAmount;
          const quantityInputElem = this.quantityInputElem;
          if (quantityInputElem) {
            quantityInputElem.value = this.quantity.toString();
          }
        }
      }

      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: this.selectedVariantId,
          quantity: this.quantity,
          properties: withCartSource(null, 'select-options-modal:onAddToCart'),
        }),
      });

      if (!response.ok) throw new Error(await response.text());
      this.logger.log('Add to cart response', response);
    } catch (error) {
      this.logger.error('Add to cart failed', error);
    } finally {
      if (shouldHidePopover) {
        this.hideModal();
      }
    }
  }
}

if (!customElements.get('select-options')) {
  customElements.define('select-options', SelectOptionsModal);
}

window.selectOptionsHandler = {
  handleRequest: (productId: string, productUrl?: string) => {
    void document.querySelector<SelectOptionsModal>('select-options')?.handleRequest(productId, productUrl);
  },
};
